import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  APP_TYPE,
  dispatch,
  FilterRoute,
  getAppliedFilters,
  getAppState,
  getSendType,
  getWorkspaceId,
  ListFilterKey,
  Payment,
  updateExtraDetails,
  updatePaymentCustomerInfo,
  useAppSelector,
  useGetPaymentFilterMutation,
  useLazyGetPaymentListQuery,
} from '@zonofi/zono-money-store';

import moment from 'moment';
import { camelCase, startCase } from 'lodash';
import { shallowEqual } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useDebounceSearch,
  usePaginatedList,
  useUrlDateRange,
} from '../../../hooks';
import {
  FIELD_OPS_ROUTES,
  PaymentStatusEnum,
  SELLER_WEBAPP_ROUTES,
} from '../../../constants';

export const usePaymentList = ({ isCustomer }: { isCustomer: boolean }) => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [searchParams] = useSearchParams();
  const customerIdFromQuery = searchParams.get('customerId');

  const [appliedStatus, setAppliedStatus] = useState<string[]>([]);
  const { debouncedSearchValue } = useDebounceSearch();
  const [isSearching, setIsSearching] = useState(false);

  const sendType = useAppSelector(getSendType);
  const appState = useAppSelector(getAppState);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const appliedFilters = useAppSelector(
    getAppliedFilters(
      customerIdFromQuery || customerId
        ? FilterRoute.CustomerPayment
        : FilterRoute.Payment
    ),
    shallowEqual
  );

  const { appliedDateRange } = useUrlDateRange({
    route:
      customerIdFromQuery || customerId
        ? FilterRoute.CustomerPayment
        : FilterRoute.Payment,
  });

  const [getPaymentListApi, getPaymentListApiResponse] =
    useLazyGetPaymentListQuery();
  const [getPaymentFilterApi, getPaymentFilterApiResponse] =
    useGetPaymentFilterMutation();

  useEffect(() => {
    getPaymentFilterApi({
      sellerWorkspaceId,
      needCustomers: !(customerIdFromQuery || customerId),
      route:
        customerIdFromQuery || customerId
          ? FilterRoute.CustomerPayment
          : FilterRoute.Payment,
    });
  }, [customerId, customerIdFromQuery]);

  useEffect(() => {
    if (sendType?.length) {
      setAppliedStatus([PaymentStatusEnum.PAID]);
    } else {
      setAppliedStatus([]);
    }
  }, [sendType?.length]);

  const statusFilters = useMemo(() => {
    const statusList = getPaymentFilterApiResponse?.data?.status ?? [];
    const filters = statusList.map((status) => ({
      id: PaymentStatusEnum[status as keyof typeof PaymentStatusEnum],
      value: startCase(camelCase(status)),
      selected: appliedStatus.includes(
        PaymentStatusEnum[status as keyof typeof PaymentStatusEnum]
      ),
    }));

    filters.unshift({
      id: PaymentStatusEnum.DEFAULT,
      value: 'All',
      selected: appliedStatus.length === 0,
    });

    return filters;
  }, [getPaymentFilterApiResponse?.data?.status, appliedStatus]);

  const fetchPaymentList = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      const doctype = appliedFilters.listFilter?.[ListFilterKey.PaymentType];
      const paymentMode =
        appliedFilters.listFilter?.[ListFilterKey.PaymentMode];
      const customerFilterIds =
        appliedFilters.listFilter?.[ListFilterKey.Customers];

      getPaymentListApi({
        endDate: `${moment(appliedDateRange?.endDate).format('YYYY-MM-DD')} 23:59:59`,
        startDate: `${moment(appliedDateRange?.startDate).format('YYYY-MM-DD')} 00:00:00`,
        sellerWorkspaceId,
        includeSettlement: true,
        ...(customerFilterIds?.length
          ? { 'inviteIds[]': customerFilterIds }
          : (customerId || customerIdFromQuery) && {
              'inviteIds[]': customerId ?? customerIdFromQuery ?? '',
            }),
        ...(appliedStatus?.length ? { 'status[]': appliedStatus } : {}),
        ...(doctype?.length ? { doctype } : {}),
        ...(paymentMode?.length ? { 'paymentMode[]': paymentMode } : {}),
        ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
        ...params,
      });
    },
    [
      appliedDateRange,
      appliedStatus,
      appliedFilters,
      debouncedSearchValue,
      customerId,
      customerIdFromQuery,
    ]
  );

  const memoizedPaymentData = useMemo(() => {
    return getPaymentListApiResponse?.data?.payments ?? [];
  }, [getPaymentListApiResponse?.data?.payments]);

  const {
    data: paymentsData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    refresh,
    isEndOfList,
  } = usePaginatedList<any>({
    fetchDataApiCall: fetchPaymentList,
    apiResponseData: memoizedPaymentData,
    isLoading: getPaymentListApiResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [
      appliedDateRange?.startDate.toString(),
      appliedDateRange?.endDate.toString(),
      appliedStatus,
      appliedFilters,
      debouncedSearchValue,
    ],
    isError: getPaymentListApiResponse.isError,
    paginationMeta: {
      startRecord: getPaymentListApiResponse?.data?.startRecord || 0,
      endRecord: getPaymentListApiResponse?.data?.endRecord || 0,
      totalCount: getPaymentListApiResponse?.data?.total || 0,
    },
  });

  const onPaymentCardPress = (item: Payment) => {
    dispatch(
      updatePaymentCustomerInfo({
        paymentRefCode: item?.paymentRefCode,
        customerName: item?.customerName,
        status: item?.status,
      })
    );
    dispatch(
      updateExtraDetails({
        customerName: item?.customerName ?? '',
        customerId: item?.inviteId ?? '',
      })
    );
    let routeToNavigate;
    if (customerId) {
      routeToNavigate = item.id ?? '';
      navigate(routeToNavigate);
    } else {
      routeToNavigate =
        appState === APP_TYPE.FIELDS_OPS
          ? FIELD_OPS_ROUTES
          : SELLER_WEBAPP_ROUTES;
      navigate(`${routeToNavigate.PAYMENT_DETAILS}/${item?.id}`);
    }
  };

  useEffect(() => {
    if (getPaymentListApiResponse.isSuccess && isSearching) {
      setIsSearching(false);
    }
  }, [getPaymentListApiResponse]);

  return {
    appliedDateRange,
    paymentsData,
    onScroll,
    pageNo,
    lastItemRef,
    isPageLoading,
    isLoading:
      pageNo === 1 &&
      (getPaymentListApiResponse?.isUninitialized ||
        getPaymentListApiResponse?.isFetching ||
        isSearching),
    onPaymentCardPress,
    isEndOfList,
    statusFilters,
    setAppliedStatus,
    refreshPaymentList: refresh,
  };
};
